// ProjectionInputsPanel.js — Input controls for projection parameters
import React from 'react';
import {
  PROFIT_TAKING_MODES, SCENARIO_PRESETS, BENCHMARK_CONFIG,
  REINVESTMENT_TENORS, COST_DEFAULTS, MARKET_CONFIG,
} from '../../lib/projections/constants';

/**
 * Input panel for all projection parameters.
 * @param {Object} props
 * @param {Object} props.inputs - current input state
 * @param {Function} props.onChange - callback to update a single input field
 * @param {boolean} props.simplified - individual investor simplified mode
 * @param {boolean} props.showAdvanced - whether advanced options are expanded
 * @param {Function} props.onToggleAdvanced - toggle advanced options
 */
const ProjectionInputsPanel = ({ inputs, onChange, simplified = false, showAdvanced = false, onToggleAdvanced }) => {
  const handleChange = (key, value) => {
    onChange(key, value);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4 space-y-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
        Projection Parameters
      </p>

      {/* Source selector */}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
          Portfolio Source
        </label>
        <select
          value={inputs.source}
          onChange={e => handleChange('source', e.target.value)}
          className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none bg-white text-zinc-700"
        >
          <option value="current">Current Portfolio</option>
          <option value="hypothetical">Hypothetical Allocation</option>
        </select>
      </div>

      {/* Hypothetical amount */}
      {inputs.source === 'hypothetical' && (
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
            Investment Amount ({inputs.baseCurrency})
          </label>
          <input
            type="number"
            value={inputs.hypotheticalAmount}
            onChange={e => handleChange('hypotheticalAmount', Number(e.target.value))}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none text-zinc-700"
            placeholder="1000000"
            min={0}
          />
        </div>
      )}

      {/* Hypothetical market allocation */}
      {inputs.source === 'hypothetical' && (
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
            Market Allocation
          </label>
          <div className="space-y-1">
            {Object.keys(MARKET_CONFIG).map(market => (
              <div key={market} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={(inputs.hypotheticalMarkets || []).includes(market)}
                  onChange={e => {
                    const current = inputs.hypotheticalMarkets || [];
                    const next = e.target.checked
                      ? [...current, market]
                      : current.filter(m => m !== market);
                    handleChange('hypotheticalMarkets', next);
                  }}
                  className="rounded-none"
                  id={`market-${market}`}
                />
                <label htmlFor={`market-${market}`} className="font-mono text-[11px] text-zinc-600">
                  {market} ({MARKET_CONFIG[market].ccy})
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profit-taking mode */}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
          Profit-Taking Mode
        </label>
        <select
          value={inputs.mode}
          onChange={e => handleChange('mode', e.target.value)}
          className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none bg-white text-zinc-700"
        >
          {Object.entries(PROFIT_TAKING_MODES).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <p className="font-mono text-[10px] text-zinc-400 mt-0.5">
          {PROFIT_TAKING_MODES[inputs.mode]?.description}
        </p>
      </div>

      {/* Annual withdrawal % (if applicable) */}
      {inputs.mode === 'annual_withdraw' && (
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
            Annual Withdrawal %
          </label>
          <input
            type="number"
            value={(inputs.withdrawalPct || 5)}
            onChange={e => handleChange('withdrawalPct', Number(e.target.value))}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none text-zinc-700"
            min={0}
            max={100}
            step={0.5}
          />
        </div>
      )}

      {/* Reinvestment tenor */}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
          Reinvestment Tenor
        </label>
        <select
          value={inputs.tenorDays}
          onChange={e => handleChange('tenorDays', Number(e.target.value))}
          className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none bg-white text-zinc-700"
        >
          {REINVESTMENT_TENORS.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Hedge ratio slider */}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
          Hedge Ratio: {(inputs.hedgeRatio * 100).toFixed(0)}%
        </label>
        <input
          type="range"
          value={inputs.hedgeRatio * 100}
          onChange={e => handleChange('hedgeRatio', Number(e.target.value) / 100)}
          className="w-full"
          min={0}
          max={100}
          step={5}
          aria-label="Hedge ratio percentage"
        />
        <div className="flex justify-between font-mono text-[9px] text-zinc-400">
          <span>0% (Unhedged)</span>
          <span>100% (Fully hedged)</span>
        </div>
      </div>

      {/* Benchmark currency */}
      {!simplified && (
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
            Benchmark Currency
          </label>
          <select
            value={inputs.baseCurrency}
            onChange={e => handleChange('baseCurrency', e.target.value)}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none bg-white text-zinc-700"
          >
            {Object.entries(BENCHMARK_CONFIG).map(([ccy, cfg]) => (
              <option key={ccy} value={ccy}>{ccy} ({cfg.label} @ {(cfg.yield * 100).toFixed(1)}%)</option>
            ))}
          </select>
        </div>
      )}

      {/* Scenario checkboxes */}
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
          Scenarios
        </label>
        <div className="space-y-1">
          {Object.entries(SCENARIO_PRESETS).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={inputs.scenarios.includes(key)}
                onChange={e => {
                  if (key === 'base') return; // Base is always included
                  const next = e.target.checked
                    ? [...inputs.scenarios, key]
                    : inputs.scenarios.filter(s => s !== key);
                  // Ensure at least 3 scenarios if possible, but allow 2 minimum
                  if (next.length >= 1 && next.includes('base')) {
                    handleChange('scenarios', next);
                  }
                }}
                disabled={key === 'base'}
                className="rounded-none"
                id={`scenario-${key}`}
              />
              <label htmlFor={`scenario-${key}`} className="font-mono text-[11px] text-zinc-600">
                {cfg.label}
              </label>
              <span className="font-mono text-[10px] text-zinc-400">- {cfg.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Adverse shock year */}
      {inputs.scenarios.includes('adverse') && (
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
            Adverse Shock Year
          </label>
          <input
            type="number"
            value={inputs.adverseShockYear}
            onChange={e => handleChange('adverseShockYear', Number(e.target.value))}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-zinc-200 rounded-none text-zinc-700"
            min={1}
            max={10}
          />
        </div>
      )}

      {/* Advanced toggle (for individual investor) */}
      {simplified && (
        <button
          onClick={onToggleAdvanced}
          className="font-mono text-[10px] text-indigo-600 hover:text-indigo-700 underline mt-2"
        >
          {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
        </button>
      )}

      {/* Editable costs (advanced) */}
      {(!simplified || showAdvanced) && (
        <div className="border-t border-zinc-100 pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              Cost Assumptions (bps)
            </label>
            <button
              onClick={() => handleChange('costs', { ...COST_DEFAULTS })}
              className="font-mono text-[10px] text-indigo-600 hover:text-indigo-700 underline"
            >
              Reset Defaults
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'platformFeeBps', label: 'Platform Fee' },
              { key: 'fxSpreadBps', label: 'Foreign Exchange Spread' },
              { key: 'forwardSpreadBps', label: 'Forward Spread' },
              { key: 'custodyBps', label: 'Custody' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="font-mono text-[9px] text-zinc-400 block">{label}</label>
                <input
                  type="number"
                  value={inputs.costs[key]}
                  onChange={e => handleChange('costs', { ...inputs.costs, [key]: Number(e.target.value) })}
                  className="w-full font-mono text-[11px] px-2 py-1 border border-zinc-200 rounded-none text-zinc-700"
                  min={0}
                  max={500}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectionInputsPanel;
